/**
 * Panneau des feux d'artifice: couleur, forme, stock de fusees.
 * Le jour, le panneau devient explicatif et le lancement est bloque: c'est une
 * regle du jeu, les feux ne partent que la nuit.
 */
import type { Inventaire, Recette } from '../../shared/types/items.ts'
import type { FormeFeu } from '../../shared/types/monde.ts'
import { objet } from '../../shared/data/objets.ts'
import { RECETTES } from '../../shared/data/recettes.ts'
import { peutPayer, quantite } from '../../shared/sim/inventaire.ts'
import { journal } from '../log/journal.ts'
import {
  Panneau,
  afficherCout,
  champCouleur,
  clicSonore,
  el,
  hex,
  pastilleCouleur,
  refusSonore,
  section,
} from './commun.ts'

/** Douze couleurs vives, lisibles dans un ciel de nuit. */
const COULEURS: readonly number[] = [
  0xff3b3b, 0xff7a1a, 0xffd21a, 0xa8e83c, 0x3ce87a, 0x2ee6c8, 0x2eb8ff, 0x3b6bff, 0x9b4dff, 0xff4dd2,
  0xff9ec4, 0xfff6d8,
]

const FORMES: readonly { id: FormeFeu; nom: string }[] = [
  { id: 'pivoine', nom: 'Pivoine' },
  { id: 'saule', nom: 'Saule' },
  { id: 'coeur', nom: 'Cœur' },
  { id: 'etoile', nom: 'Étoile' },
]

const SVG = 'http://www.w3.org/2000/svg'

/** Message affiche le jour. La regle vient de PHASE_NUIT_DEBUT (20 h 30). */
const MESSAGE_JOUR = 'Les feux d’artifice ne se lancent que la nuit. Reviens après 20 h 30.'

export class PanneauFeux extends Panneau {
  /** Le joueur lance une fusee. */
  onLancer: ((couleur: number, forme: FormeFeu) => void) | null = null
  /** Le joueur fabrique des fusees. */
  onFabriquer: (() => void) | null = null

  private inv: Inventaire = {}
  private nuit = false
  private couleur = COULEURS[2] as number
  private forme: FormeFeu = 'pivoine'
  private vignettes: { e: SVGSVGElement; forme: FormeFeu }[] = []
  private pastilles: { e: HTMLElement; c: number }[] = []

  constructor(parent: HTMLElement) {
    super(parent, 'feu', 'Feux d’artifice', '🎆', 'panneau-fond--feux')
  }

  ouvrir(inv: Inventaire, nuit: boolean): void {
    this.inv = inv
    this.nuit = nuit
    this.construire()
    this.afficher(nuit ? 'nuit' : 'jour (lancement bloque)')
  }

  /** Met a jour le stock et l'autorisation sans fermer le panneau. */
  rafraichir(inv: Inventaire, nuit: boolean): void {
    this.inv = inv
    this.nuit = nuit
    if (this.ouvert) this.construire()
  }

  private construire(): void {
    this.viderCorps()
    this.viderPied()
    this.vignettes = []
    this.pastilles = []

    if (!this.nuit) {
      const avis = el('div', 'banniere banniere--info')
      avis.append(el('span', 'banniere__icone', '🌞'))
      avis.append(el('span', 'banniere__texte', MESSAGE_JOUR))
      this.corps.append(avis)
    }

    // ------------------------------------------------------------ couleur
    const sCouleur = section('Couleur', '🎨')
    const zone = el('div', 'couleurs')
    for (const c of COULEURS) {
      const p = pastilleCouleur(c, c === this.couleur, () => this.choisirCouleur(c))
      this.pastilles.push({ e: p, c })
      zone.append(p)
    }
    zone.append(champCouleur(this.couleur, (c) => this.choisirCouleur(c)))
    sCouleur.append(zone)
    this.corps.append(sCouleur)

    // -------------------------------------------------------------- forme
    const sForme = section('Forme', '✨')
    const listeFormes = el('div', 'formes')
    for (const f of FORMES) {
      const b = el('button', 'forme')
      b.type = 'button'
      b.title = f.nom
      if (f.id === this.forme) b.classList.add('forme--active')
      const v = this.vignette(f.id)
      this.vignettes.push({ e: v, forme: f.id })
      b.append(v)
      b.append(el('span', 'forme__nom', f.nom))
      b.addEventListener('click', () => {
        clicSonore()
        this.forme = f.id
        journal.input('feu', `forme=${f.id}`)
        for (const enfant of Array.from(listeFormes.children)) enfant.classList.remove('forme--active')
        b.classList.add('forme--active')
      })
      listeFormes.append(b)
    }
    sForme.append(listeFormes)
    this.corps.append(sForme)

    // ------------------------------------------------------------- stock
    const stock = quantite(this.inv, 'fusee')
    const sStock = section('Fusées', objet('fusee').icone)
    const ligne = el('div', 'stock')
    ligne.append(el('span', 'stock__nombre', String(stock)))
    ligne.append(el('span', 'stock__texte', stock === 1 ? 'fusée en réserve' : 'fusées en réserve'))
    sStock.append(ligne)

    const recette = this.recetteFusee()
    if (recette) {
      sStock.append(el('div', 'carte__etiquette', `Fabriquer ${recette.quantite} fusée${recette.quantite > 1 ? 's' : ''}`))
      sStock.append(afficherCout(recette.cout, this.inv))
      const possible = peutPayer(this.inv, recette.cout)
      const bFab = el('button', 'bouton bouton--sombre')
      bFab.type = 'button'
      bFab.textContent = '🧨 Fabriquer'
      bFab.addEventListener('click', () => {
        if (!possible) {
          refusSonore()
          journal.input('feu', 'fabrication refusee: ressources manquantes')
          return
        }
        clicSonore()
        journal.input('feu', `fabrication de ${recette.id}`)
        this.onFabriquer?.()
      })
      if (!possible) bFab.classList.add('bouton--faux-desactive')
      sStock.append(bFab)
    } else {
      sStock.append(el('p', 'note', 'Aucune recette de fusée n’est connue pour le moment.'))
    }
    this.corps.append(sStock)

    // --------------------------------------------------------------- pied
    const lancer = el('button', 'bouton bouton--vert feux__lancer')
    lancer.type = 'button'
    lancer.textContent = '🎆 Lancer'
    const bloque = !this.nuit || stock <= 0
    lancer.disabled = bloque
    lancer.title = !this.nuit ? MESSAGE_JOUR : stock <= 0 ? 'Il faut au moins une fusée.' : 'Lancer une fusée'
    lancer.addEventListener('click', () => {
      if (bloque) return
      clicSonore()
      journal.input('feu', `lancement couleur=${hex(this.couleur)} forme=${this.forme}`)
      this.onLancer?.(this.couleur, this.forme)
    })
    this.pied.append(lancer)
    if (bloque) {
      this.pied.append(
        el('span', 'pied__astuce', !this.nuit ? '🌙 Reviens à la nuit tombée.' : '🧨 Fabrique d’abord une fusée.'),
      )
    }
  }

  private choisirCouleur(c: number): void {
    this.couleur = c
    journal.input('feu', `couleur=${hex(c)}`)
    for (const { e, c: cc } of this.pastilles) {
      const est = cc === c
      e.classList.toggle('pastille--active', est)
      e.setAttribute('aria-pressed', est ? 'true' : 'false')
    }
    for (const { e } of this.vignettes) e.style.color = hex(c)
  }

  private recetteFusee(): Recette | null {
    for (const r of RECETTES) if (r.produit === 'fusee') return r
    return null
  }

  // ------------------------------------------------------------- vignettes

  /** Petite illustration SVG de la forme, teintee par la couleur choisie. */
  private vignette(forme: FormeFeu): SVGSVGElement {
    const svg = document.createElementNS(SVG, 'svg')
    svg.setAttribute('viewBox', '0 0 48 48')
    svg.setAttribute('class', 'forme__vignette')
    svg.style.color = hex(this.couleur)

    const trait = (d: string, largeur = 2): SVGPathElement => {
      const p = document.createElementNS(SVG, 'path')
      p.setAttribute('d', d)
      p.setAttribute('fill', 'none')
      p.setAttribute('stroke', 'currentColor')
      p.setAttribute('stroke-width', String(largeur))
      p.setAttribute('stroke-linecap', 'round')
      svg.append(p)
      return p
    }
    const point = (x: number, y: number, r: number): void => {
      const c = document.createElementNS(SVG, 'circle')
      c.setAttribute('cx', String(x))
      c.setAttribute('cy', String(y))
      c.setAttribute('r', String(r))
      c.setAttribute('fill', 'currentColor')
      svg.append(c)
    }

    if (forme === 'pivoine') {
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2
        const x = 24 + Math.cos(a) * 16
        const y = 24 + Math.sin(a) * 16
        trait(`M24 24 L${x.toFixed(1)} ${y.toFixed(1)}`, 1.6)
        point(x, y, 2.2)
      }
      point(24, 24, 3)
    } else if (forme === 'saule') {
      for (let i = 0; i < 7; i++) {
        const a = -Math.PI + (i / 6) * Math.PI
        const mx = 24 + Math.cos(a) * 15
        const my = 20 + Math.sin(a) * 11
        trait(`M24 18 Q${mx.toFixed(1)} ${my.toFixed(1)} ${(mx + Math.cos(a) * 3).toFixed(1)} 42`, 1.6)
        point(mx + Math.cos(a) * 3, 42, 1.8)
      }
      point(24, 18, 2.6)
    } else if (forme === 'coeur') {
      const c = document.createElementNS(SVG, 'path')
      c.setAttribute(
        'd',
        'M24 40 C6 27 8 12 17 12 C21 12 24 15 24 18 C24 15 27 12 31 12 C40 12 42 27 24 40 Z',
      )
      c.setAttribute('fill', 'currentColor')
      svg.append(c)
      point(12, 8, 1.6)
      point(38, 9, 1.4)
    } else {
      const branches: string[] = []
      for (let i = 0; i < 10; i++) {
        const r = i % 2 === 0 ? 18 : 7.5
        const a = -Math.PI / 2 + (i / 10) * Math.PI * 2
        branches.push(`${(24 + Math.cos(a) * r).toFixed(1)},${(24 + Math.sin(a) * r).toFixed(1)}`)
      }
      const poly = document.createElementNS(SVG, 'polygon')
      poly.setAttribute('points', branches.join(' '))
      poly.setAttribute('fill', 'currentColor')
      svg.append(poly)
    }
    return svg
  }

  override detruire(): void {
    this.onLancer = null
    this.onFabriquer = null
    this.vignettes = []
    this.pastilles = []
    super.detruire()
  }
}
